import { useLayoutEffect, useCallback, useEffect, useRef, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { FlatList, ScrollView } from 'react-native';
import { isDatabaseUnavailableError } from '../../shared/localDb';
import { scrollPaginatedListToTop, type PaginatedListScrollRefs } from '../ui/paginatedListScroll';

export type PaginatedListPageSize = 5 | 10 | 25 | 50;
const PAGE_SIZE_OPTIONS: PaginatedListPageSize[] = [5, 10, 25, 50];

export type UsePaginatedRemoteListParams<T, Q> = {
  storageKey: string;
  defaultPageSize?: PaginatedListPageSize;
  criteriaKey: string;
  query: Q;
  fetchPage: (args: { limit: number; offset: number; query: Q }) => Promise<T[]>;
  count: (query: Q) => Promise<number>;
  enabled?: boolean;
  logLabel?: string;
};

export function usePaginatedRemoteList<T, Q>({
  storageKey,
  defaultPageSize = 10,
  criteriaKey,
  query,
  fetchPage,
  count,
  enabled = true,
  logLabel = 'List',
}: UsePaginatedRemoteListParams<T, Q>) {
  const flatListRef = useRef<FlatList<T>>(null);
  const scrollViewRef = useRef<ScrollView>(null);
  const loadOpRef = useRef(0);
  const loadInFlightRef = useRef(false);
  const scrollListToTopRef = useRef(false);

  const fetchPageRef = useRef(fetchPage);
  const countRef = useRef(count);
  const queryRef = useRef(query);
  fetchPageRef.current = fetchPage;
  countRef.current = count;
  queryRef.current = query;

  const [items, setItems] = useState<T[]>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSizeState] = useState<PaginatedListPageSize>(defaultPageSize);
  const [sizeHydrated, setSizeHydrated] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(enabled);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // Count cacheado por criteriaKey: paginar o re-cargar con los mismos filtros
  // reusa el total y no repite la query de count. Mutations usan
  // reloadFromStart/refreshTotalCount que fuerzan recount.
  const lastCountCriteriaRef = useRef<string | null>(null);
  const totalCountRef = useRef(0);

  const scrollRefs: PaginatedListScrollRefs<T> = {
    flatListRef,
    scrollViewRef,
  };

  useEffect(() => {
    let alive = true;
    void AsyncStorage.getItem(storageKey).then((raw) => {
      if (!alive) return;
      const n = raw != null ? Number(raw) : defaultPageSize;
      if (PAGE_SIZE_OPTIONS.includes(n as PaginatedListPageSize)) {
        setPageSizeState(n as PaginatedListPageSize);
      }
      setSizeHydrated(true);
    });
    return () => {
      alive = false;
    };
  }, [defaultPageSize, storageKey]);

  useLayoutEffect(() => {
    setPageIndex(0);
    scrollListToTopRef.current = true;
  }, [criteriaKey]);

  const setPageSize = useCallback(
    (size: number) => {
      const validated = PAGE_SIZE_OPTIONS.includes(size as PaginatedListPageSize)
        ? (size as PaginatedListPageSize)
        : defaultPageSize;
      setPageSizeState(validated);
      void AsyncStorage.setItem(storageKey, String(validated));
      setPageIndex(0);
      scrollListToTopRef.current = true;
    },
    [defaultPageSize, storageKey]
  );

  const fetchPageAndSet = useCallback(
    async (offset: number, errorLabel: string, opts?: { freshCount?: boolean }) => {
      const opId = ++loadOpRef.current;
      loadInFlightRef.current = true;
      setLoading(true);
      try {
        const activeQuery = queryRef.current;
        const needCount =
          opts?.freshCount === true || lastCountCriteriaRef.current !== criteriaKey;
        const [rows, freshTotal] = await Promise.all([
          fetchPageRef.current({ limit: pageSize, offset, query: activeQuery }),
          needCount ? countRef.current(activeQuery) : Promise.resolve(null),
        ]);
        if (opId !== loadOpRef.current) return;
        setError(null);
        setItems(rows);
        const total = freshTotal ?? totalCountRef.current;
        if (freshTotal != null) {
          totalCountRef.current = freshTotal;
          setTotalCount(freshTotal);
          lastCountCriteriaRef.current = criteriaKey;
        }
        setHasMore(offset + rows.length < total);
        if (scrollListToTopRef.current) {
          scrollPaginatedListToTop(scrollRefs, true);
          scrollListToTopRef.current = false;
        }
      } catch (error) {
        if (!isDatabaseUnavailableError(error)) {
          console.warn(`[${logLabel}] ${errorLabel}`, error);
        }
        if (opId === loadOpRef.current) {
          setError(error instanceof Error ? error.message : errorLabel);
          // Conservar el snapshot visible: un fallo de recarga no borra los
          // datos ya mostrados. La UI muestra un estado de error con retry.
          lastCountCriteriaRef.current = null;
        }
      } finally {
        if (opId === loadOpRef.current) {
          loadInFlightRef.current = false;
          setLoading(false);
        }
      }
    },
    [criteriaKey, logLabel, pageSize],
  );

  const load = useCallback(async () => {
    if (!enabled || !sizeHydrated) return;
    await fetchPageAndSet(pageIndex * pageSize, 'No se pudo cargar el listado');
  }, [enabled, fetchPageAndSet, pageIndex, sizeHydrated]);

  useEffect(() => {
    if (!sizeHydrated) return;
    void load();
  }, [criteriaKey, load, sizeHydrated]);

  const loadNextPage = useCallback(() => {
    if (!hasMore || loading) return;
    setPageIndex((p) => p + 1);
  }, [hasMore, loading]);

  const loadPreviousPage = useCallback(() => {
    if (loading) return;
    setPageIndex((p) => (p <= 0 ? p : p - 1));
  }, [loading]);

  const goToPage = useCallback(
    (page: number) => {
      if (loading) return;
      const totalPages = totalCount > 0 ? Math.ceil(totalCount / pageSize) : 1;
      const clamped = Math.max(0, Math.min(page, totalPages - 1));
      if (clamped === pageIndex) return;
      setPageIndex(clamped);
      scrollListToTopRef.current = true;
    },
    [loading, totalCount, pageSize, pageIndex],
  );

  const refreshTotalCount = useCallback(async () => {
    try {
      const n = await countRef.current(queryRef.current);
      totalCountRef.current = n;
      lastCountCriteriaRef.current = criteriaKey;
      setTotalCount(n);
    } catch (error) {
      if (!isDatabaseUnavailableError(error)) {
        console.warn(`[${logLabel}] No se pudo contar ítems`, error);
      }
      setTotalCount(0);
    }
  }, [criteriaKey, logLabel]);

  const resetToFirstPage = useCallback(() => {
    setPageIndex(0);
    scrollListToTopRef.current = true;
  }, []);

  const reloadFromStart = useCallback(async () => {
    if (!enabled || !sizeHydrated) return;
    if (loadInFlightRef.current) return;
    setPageIndex(0);
    scrollListToTopRef.current = true;
    await fetchPageAndSet(0, 'No se pudo recargar el listado', { freshCount: true });
  }, [enabled, fetchPageAndSet, sizeHydrated]);

  return {
    flatListRef,
    scrollViewRef,
    items,
    pageIndex,
    pageSize,
    setPageSize,
    hasMore,
    loading,
    error,
    totalCount,
    load,
    loadNextPage,
    loadPreviousPage,
    goToPage,
    refreshTotalCount,
    resetToFirstPage,
    reloadFromStart,
    sizeHydrated,
  };
}
